import { useContext, useEffect, useMemo } from 'react';

import { DEFAULT_FIAS_ID } from '@src/constants';
import { FormIFLGetPricesFull } from '@src/constants/request-data';
import { useRequest } from '@src/hooks';
import { IFLHouseActionTypes, Store } from '@src/store';
import { GetPrices } from '@src/types';
import { subObjectConverter } from '@src/utils';

import {
  createRisksFromPresetData,
  createSubProductsFromPresetData,
} from '../utils';

export const useRequestData = (
  autoInitPromo: boolean,
  localPromo: string | undefined
) => {
  const {
    state: {
      stateFormIFLHouse: {
        selectedIProduct,
        presetData,
        risks,
        selectedRegion,
        selectedBuildingMaterialCode,
        insuranceSubproducts,
        dadaValue,
        insuranceSum,
      },
      stateAuth: { authTokens },
      statePresetSetting: { isPresetSettingDisplayed },
    },
    dispatch,
  } = useContext(Store);

  useEffect(() => {
    if (!!presetData && presetData.buildingMaterial) {
      dispatch({
        type: IFLHouseActionTypes.SetSelectedBuildingMaterialCode,
        payload: presetData.buildingMaterial,
      });
    }
  }, [presetData]);

  const { res: risksRes } = useRequest(
    'formIFLGetRisks',
    'post',
    '/v1/references/get-risks',
    {
      productCode: selectedIProduct?.code,
    },
    [selectedIProduct?.code]
  );

  useEffect(() => {
    if (!risks && !!presetData && !!risksRes) {
      dispatch({
        type: IFLHouseActionTypes.SetRisks,
        payload: createRisksFromPresetData(presetData, risksRes.risks),
      });
    }
  }, [risks, presetData, risksRes]);

  const activeRisksCodes = useMemo(
    () =>
      risks
        ?.filter((riskItem) => {
          if (riskItem.active) {
            return riskItem;
          }
        })
        .map((filteredRiskItem) => filteredRiskItem.code) || [],
    [risks]
  );

  const { res: subobjectsRes, error: subobjectsError } = useRequest(
    'formIFLGetSubproducts',
    'post',
    '/v1/references/get-subobjects',
    {
      productCode: selectedIProduct?.code,
      region: selectedRegion?.region,
      risks: activeRisksCodes,
      buildingMaterial: selectedBuildingMaterialCode,
    },
    [
      activeRisksCodes,
      selectedBuildingMaterialCode,
      selectedIProduct?.code,
      selectedRegion?.region,
      authTokens?.authorization?.accessToken,
    ],
    !(activeRisksCodes.length && !!presetData),
    authTokens?.authorization?.accessToken
  );

  useEffect(() => {
    if (!!presetData && !!subobjectsRes) {
      dispatch({
        type: IFLHouseActionTypes.SetInsuranceSubproducts,
        payload: createSubProductsFromPresetData(presetData, subobjectsRes),
      });
      dispatch({
        type: IFLHouseActionTypes.SetCurrentSumMinLimit,
        payload:
          subobjectsRes.totalMinLimit ??
          selectedIProduct?.minProductLimit ??
          '0',
      });
      dispatch({
        type: IFLHouseActionTypes.SetCurrentSumMaxLimit,
        payload:
          subobjectsRes.totalMaxLimit ??
          selectedIProduct?.maxProductLimit ??
          '0',
      });

      dispatch({
        type: IFLHouseActionTypes.SetInsuranceSum,
        payload: presetData.subObjects.reduce(
          (acc, { defaultInsuranceSum }) =>
            (acc += Number(defaultInsuranceSum)),
          0
        ),
      });
    }
  }, [presetData, subobjectsRes]);

  const {
    isLoading: isPricesLoading,
    error: pricesError,
    res: pricesRes,
  } = useRequest<GetPrices>(
    FormIFLGetPricesFull,
    'post',
    '/v1/subscription/get-prices',
    {
      risks: activeRisksCodes,
      productCode: selectedIProduct?.code,
      insuranceSum: Number(insuranceSum),
      personProperties: {
        subObjects: insuranceSubproducts?.subObjects
          ? subObjectConverter(insuranceSubproducts.subObjects)
          : [],
        address: {
          regionCode: selectedRegion?.region,
          address: dadaValue?.value || '',
          addressCode: dadaValue?.data?.fias_id || DEFAULT_FIAS_ID,
        },
      },
      returnMinDuration: false,
      buildingMaterial: selectedBuildingMaterialCode,
      promoCode: autoInitPromo ? localPromo : undefined,
    },
    [
      activeRisksCodes,
      selectedRegion?.region,
      insuranceSubproducts?.subObjects,
      localPromo,
      autoInitPromo,
      dadaValue?.value,
      dadaValue?.data?.fias_id,
      selectedIProduct?.code,
      insuranceSum,
      selectedBuildingMaterialCode,
      String(isPresetSettingDisplayed),
    ],
    !(activeRisksCodes && insuranceSubproducts?.subObjects.length),
    authTokens?.authorization?.accessToken
  );

  return {
    subobjectsError,
    isPricesLoading,
    pricesError,
    pricesRes,
  };
};
